import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { tokens } from "../../theme";
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import {
  ownerIngestApi,
  type ContentReachView,
  type IngestJobView,
  type IngestReadiness,
  type OwnerShell,
  type OwnerTitleRow,
  type VodTitleRow,
  foyerApi,
} from "../../http/foyer";
import type { ContentRentalSummary } from "@shared/content-rental";
import {
  AGREEMENT_LABEL,
  type AgreementStatus,
} from "@shared/content-agreement";
import {
  ingestStepState,
  type IngestPhase,
  type IngestStepState,
} from "@shared/ingest-phases";
import { uploadInBlocks } from "../../lib/blockUpload";
import LocalisedImagesPanel from "../../components/distribution/LocalisedImagesPanel";
import {
  assetToDraft,
  draftToUpdate,
  emptiedCollections,
} from "@shared/asset-draft";

/**
 * One title, and the master behind it (`CONTENT-9`).
 *
 * ## 🚨 The one rule: no fake progress, ever
 *
 * Everything on this page is either a byte count the browser MEASURED while
 * sending, or a phase the server actually recorded. Nothing is interpolated,
 * nothing is animated towards a value it has not reached, and a job whose state
 * is unknown says unknown.
 *
 * A mocked rental count is a placeholder a partner forgives. A progress bar
 * advancing while nothing happens is a lie about their material, and it costs
 * the relationship exactly once.
 *
 * ## Five phases, not four
 *
 * The brief says upload → transcode → ingest → ready. `Queued` earns its place
 * as a fifth: it is the difference between "your film is being worked on" and
 * "your film is behind eleven others", and it is the phase a studio will ask
 * about. Collapsing it into Transcoding turns a capacity question into a
 * support ticket.
 *
 * The ladder's step states come from `ingestStepState`, shared with the
 * operator panel so the two surfaces cannot classify the same job two ways.
 */

interface ShellContext {
  shell: OwnerShell;
}

/**
 * The steps a supplier is shown, in the words a supplier uses.
 *
 * ## 🚨 Review is a STEP here and NOT a phase, deliberately
 *
 * `IngestJob.hold` keeps "no human has watched this yet" out of
 * `INGEST_PHASES`, and that decision is load-bearing: the phase machine
 * interprets what an UPSTREAM said, and no upstream says anything about
 * review. Nothing here changes that — the hold stays a hold, and this ladder
 * (a DISPLAY concern) renders it as the first rung.
 *
 * It has to. Until it did, a title held for screening showed the supplier
 * **"Queued — your master is in place and waiting for a transcoder"**, which
 * is false twice over: nothing is queued for a transcoder, and the thing it is
 * actually waiting for is a person. A supplier reading that would wait for
 * capacity that was never the constraint.
 *
 * ## And `completed` was missing entirely
 *
 * The ladder stopped at `publishing`, so the last thing a supplier ever saw
 * was a step in progress — with no rung that says the title is live. "Did it
 * work?" was unanswerable from the one screen built to answer it.
 */
type LadderStep =
  | { kind: "review"; label: string; detail: string }
  | { kind: "phase"; phase: IngestPhase; label: string; detail: string };

export const LADDER: LadderStep[] = [
  {
    kind: "review",
    label: "Waiting for review",
    detail:
      "Nenda is watching the material. Nothing is sent for encoding until it is approved.",
  },
  {
    kind: "phase",
    phase: "queued",
    label: "Queued",
    detail: "Your master is in place and waiting for a transcoder.",
  },
  {
    kind: "phase",
    phase: "transcoding",
    label: "Transcoding",
    detail: "Producing the versions guests will actually play.",
  },
  {
    kind: "phase",
    phase: "ingesting",
    label: "Ingesting",
    detail: "Shipping the package to the delivery network.",
  },
  {
    kind: "phase",
    phase: "publishing",
    label: "Publishing",
    detail: "Waiting for the delivery network to return its playable links.",
  },
  {
    kind: "phase",
    phase: "completed",
    label: "Published",
    detail: "Live in the catalogue.",
  },
];

/**
 * What a step shows, for a ladder that is not purely phases.
 *
 * ⚠️ Two cases `ingestStepState` cannot answer on its own:
 *
 * **Review** is a hold, so it has no phase to look up. `awaiting_review` is the
 * step a person is standing on; `incomplete` means the SUPPLIER still owes
 * something, so review has not begun; no hold at all means nothing is waiting
 * on review — which is also true for a trusted supplier whose upload never
 * held, and reading "done" is honest for them rather than a claim that somebody
 * watched it.
 *
 * **Published** is terminal, and `ingestStepState` calls the phase a job is
 * sitting in "current" — correct for every rung except the last one, where a
 * spinner would run forever on a title that is finished.
 */
export function ladderStepState(
  job: IngestJobView,
  step: LadderStep
): IngestStepState {
  if (step.kind === "review") {
    if (job.hold?.reason === "awaiting_review") return "current";
    if (job.hold?.reason === "incomplete") return "waiting";
    return "done";
  }
  if (step.phase === "completed" && job.phase === "completed") return "done";

  const state = ingestStepState(job, step.phase);
  /**
   * 🚨 A HELD job has not started any phase work, so no phase rung is current.
   *
   * `queued` is where a held job SITS — the dispatcher skips it
   * (`leaseNextQueued` filters on `hold`) — and `ingestStepState` reports the
   * phase a job is in, which is right for it and wrong here. Without this the
   * ladder showed TWO spinners at once: "Waiting for review" and "Queued",
   * telling a supplier both that a person is looking at it and that a
   * transcoder is about to pick it up. Only one of those was true.
   *
   * A rung already in `phaseLog` still reads done — history is history.
   */
  if (job.hold && state === "current") return "waiting";
  return state;
}

/** Plain words for an incident code. Never raw codes on a partner's screen. */
const INCIDENT_WORDS: Record<string, string> = {
  dispatch_failed: "We could not hand the file to the transcoder",
  transcode_failed: "The transcoder rejected this file",
  phase_timeout: "A step took longer than allowed and was stopped",
  publish_failed: "Publishing did not complete",
  upstream_unreachable: "A service we depend on did not answer",
};

function StepMark({ state }: { state: IngestStepState }) {
  if (state === "current") return <CircularProgress size={13} />;
  const colour =
    state === "failed"
      ? tokens.error
      : state === "done"
        ? tokens.brand
        : tokens.divider;
  return (
    <Box
      sx={{
        width: 13,
        height: 13,
        borderRadius: "50%",
        background: state === "waiting" ? "transparent" : colour,
        border: `2px solid ${colour}`,
      }}
    />
  );
}

export default function SupplierTitlePage() {
  useOutletContext<ShellContext>();
  const { assetId = "" } = useParams();

  const [title, setTitle] = useState<OwnerTitleRow | null>(null);
  /**
   * The title IN FULL, for artwork (`ART-8`).
   *
   * `listOwnTitles` answers summaries and a summary carries no `images`, so
   * the row above cannot draw or edit artwork. Read separately rather than
   * widening the list: a list page has no use for every image on every row.
   */
  const [full, setFull] = useState<VodTitleRow | null>(null);
  const [artworkPanel, setArtworkPanel] = useState(false);
  /** Delete asks twice: it does not come back on its own. */
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();

  const [jobs, setJobs] = useState<IngestJobView[]>([]);
  const [readiness, setReadiness] = useState<IngestReadiness | null>(null);
  const [reach, setReach] = useState<ContentReachView | null>(null);
  const [reachError, setReachError] = useState<string | null>(null);
  const [rentals, setRentals] = useState<ContentRentalSummary | null>(null);
  const [rentalError, setRentalError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Bytes actually sent, out of bytes selected. Measured, never estimated. */
  const [sent, setSent] = useState<{ done: number; total: number } | null>(
    null
  );
  const [terms, setTerms] = useState<AgreementStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    const res = await ownerIngestApi.listIngestJobs(assetId);
    setJobs(res.jobs);
    setReadiness(res.readiness);
    return res.jobs;
  }, [assetId]);

  useEffect(() => {
    let live = true;
    Promise.all([foyerApi.listOwnTitles(), refresh()])
      .then(([list]) => {
        if (!live) return;
        setTitle(list.titles.find((t) => t.assetId === assetId) ?? null);
      })
      .then(() =>
        assetId
          ? foyerApi.getOwnTitle(assetId).then((row) => live && setFull(row))
          : undefined
      )
      .catch(
        (e) => live && setError(e instanceof Error ? e.message : String(e))
      );
    return () => {
      live = false;
    };
  }, [assetId, refresh]);

  useEffect(() => {
    let live = true;
    foyerApi
      .contentReach(assetId)
      .then((r) => live && setReach(r))
      // Kept apart from the page error on purpose: the reach registry fails
      // CLOSED, so an error here means "we cannot answer", and it must not be
      // allowed to read as "nowhere". A supplier is paid on this number.
      .catch(
        (e) => live && setReachError(e instanceof Error ? e.message : String(e))
      );
    return () => {
      live = false;
    };
  }, [assetId]);

  useEffect(() => {
    let live = true;
    foyerApi
      .ownerAgreements()
      .then((t) => live && setTerms(t))
      .catch(() => {
        /* the server gates the upload regardless; this only draws it */
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    foyerApi
      .contentRentals(assetId)
      .then((r) => live && setRentals(r))
      .catch(
        (e) =>
          live && setRentalError(e instanceof Error ? e.message : String(e))
      );
    return () => {
      live = false;
    };
  }, [assetId]);

  // Poll only while something is live, and stop the moment it is terminal. A
  // poll that keeps running against a finished job is a request per five
  // seconds, per open tab, forever.
  const live = jobs.some(
    (j) => j.phase !== "completed" && j.phase !== "failed"
  );
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => {
      refresh().catch(() => {
        /* a failed poll is not a failed job; the next tick tries again */
      });
    }, 5_000);
    return () => clearInterval(timer);
  }, [live, refresh]);

  async function upload(file: File) {
    setError(null);
    setBusy(true);
    try {
      // Refused here as well as on the server, because the server's refusal
      // arrives after the file has been chosen and the honest moment to say
      // "not this kind" is now.
      if (!file.name.toLowerCase().endsWith(".mp4")) {
        setError("We can only take .mp4 masters at the moment.");
        return;
      }
      const { job } = await ownerIngestApi.startIngest(assetId, {
        extension: "mp4",
      });
      const target = await ownerIngestApi.ingestUploadUrl(job.id);
      setSent({ done: 0, total: file.size });
      await uploadInBlocks(target.uploadUrl, file, {
        contentType: "video/mp4",
        onProgress: (done, total) => setSent({ done, total }),
      });
      // Verified by the server reading the blob's size, not believed from here.
      await ownerIngestApi.ingestUploaded(job.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSent(null);
      setBusy(false);
    }
  }

  async function retry(job: IngestJobView) {
    setBusy(true);
    try {
      await ownerIngestApi.retryIngest(job.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function withdraw(undo: boolean) {
    setBusy(true);
    try {
      if (undo) await foyerApi.undoWithdrawOwnTitle(assetId);
      else await foyerApi.withdrawOwnTitle(assetId);
      const list = await foyerApi.listOwnTitles();
      setTitle(list.titles.find((t) => t.assetId === assetId) ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Delete the title. Only reachable once it is WITHDRAWN.
   *
   * The two verbs are separate on the server and stay separate here: the route
   * refuses a live title with `not_withdrawn`, and `unrestricted` — no rights
   * window at all — counts as live. Offering one button that did both would
   * mean a supplier could destroy a title that is on air in one click, having
   * been told it was one thing.
   *
   * The delete is SOFT: dark immediately, purged upstream only after a grace
   * period, so the sentence below promises what actually happens.
   */
  async function remove() {
    setBusy(true);
    try {
      await foyerApi.deleteOwnTitle(assetId);
      navigate("/supplier/titles");
    } catch (e) {
      const err = e as { code?: string; message?: string };
      setError(
        err.code === "not_withdrawn"
          ? "Withdraw this title before deleting it — a title that is still offered cannot be removed."
          : err.code === "title_in_use"
            ? "This title is still placed somewhere, so it cannot be deleted yet. Your Nenda contact can take it out of its packages."
            : err.code === "children_would_be_orphaned"
              ? "This has episodes or seasons under it. Delete those first, or they would be left with no parent."
              : err.code === "owner_delete_unavailable"
                ? "Deleting is not switched on for this environment yet."
                : (err.message ?? "The title could not be deleted.")
      );
    } finally {
      setBusy(false);
    }
  }

  /** What a retry would actually resume, so the button can say it. */
  function retryLabel(job: IngestJobView): string {
    const failedIn = job.phaseLog[job.phaseLog.length - 1]?.phase;
    const step = LADDER.find((s) => s.kind === "phase" && s.phase === failedIn);
    // A phase-aware retry presented as a generic "Try again" invites a second
    // transcode of the same film.
    return step ? `Retry from ${step.label}` : "Retry";
  }

  // Derived from the title, never stored: a window's meaning moves with the
  // clock, and `unrestricted` is deliberately NOT withdrawn - no rights row
  // means offered everywhere, forever.
  const withdrawn = title?.availability?.state === "expired";
  // CONTENT-28: a title Nenda has licensed, attributed to this studio. They
  // follow it here and change nothing; the server refuses every write anyway.
  const locked = title?.licensed === true;

  return (
    // The page root doubles as a native fieldset: `disabled` turns off every
    // button and input inside at once, so a control added later is covered
    // too, without a wrapper re-indenting the whole page. Links are not form
    // controls and keep working ("← All titles").
    <Stack
      spacing={3}
      component="fieldset"
      disabled={locked}
      sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
    >
      <Box>
        <Link
          to="/supplier/titles"
          style={{ color: tokens.inkMuted, fontSize: 13 }}
        >
          ← All titles
        </Link>
        <Typography sx={{ fontSize: 24, mt: 1 }}>
          {title?.originalTitle || assetId}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {locked && (
        <Alert severity="info" data-testid="title-licensed">
          This is a title Nenda has licensed. You can follow it and its numbers
          here, but changes to it - including withdrawing it - go through Nenda.
        </Alert>
      )}

      <Box
        sx={{
          background: tokens.surface,
          border: `1px solid ${tokens.divider}`,
          borderRadius: 2,
          p: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Availability</Typography>
        {withdrawn ? (
          <>
            <Typography sx={{ color: tokens.inkMuted, fontSize: 14, mb: 2 }}>
              Withdrawn. This title is not offered in any market.
              {/* Said plainly rather than implied: the play gate reads package
                  membership, so a withdrawn title remains playable to anyone
                  holding its id until it leaves every package. Promising
                  otherwise is a promise the model does not keep. */}{" "}
              It can still be played by anyone who already has a direct link to
              it, until we remove it from the packages it is in.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => void withdraw(true)}
                sx={{ color: tokens.brand, borderColor: tokens.divider }}
              >
                Put it back
              </Button>
              {/* Only once withdrawn — see `remove`. A second press is asked
                  for because this one does not come back on its own. */}
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() =>
                  confirmDelete ? void remove() : setConfirmDelete(true)
                }
                sx={{ color: tokens.error, borderColor: tokens.error }}
                data-testid="title-delete"
              >
                {confirmDelete ? "Yes — delete it" : "Delete this title"}
              </Button>
            </Stack>
            {confirmDelete && (
              <Typography
                sx={{ color: tokens.inkMuted, fontSize: 13, mt: 1 }}
                data-testid="title-delete-warning"
              >
                It goes dark straight away. We keep it recoverable for a short
                grace period, then it is removed from the catalogue for good.
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography sx={{ color: tokens.inkMuted, fontSize: 14, mb: 2 }}>
              {title?.availability?.state === "unrestricted"
                ? // The dangerous default, named: no rights row means available
                  // everywhere, forever - not "not set up yet".
                  "Offered everywhere, with no end date. If your licence has limits, tell your Nenda contact so we can record them."
                : "Offered under the licence window we have on record."}
            </Typography>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => void withdraw(false)}
              // Foyer's own danger colour, which a partner's brand cannot move.
              sx={{ color: tokens.error, borderColor: tokens.error }}
            >
              Withdraw this title
            </Button>
          </>
        )}
      </Box>

      <Box
        sx={{
          background: tokens.surface,
          border: `1px solid ${tokens.divider}`,
          borderRadius: 2,
          p: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Master file</Typography>
        <Typography sx={{ color: tokens.inkMuted, fontSize: 14, mb: 2 }}>
          One .mp4 per title. It goes straight to our storage from your browser
          — it does not pass through this page — and processing starts as soon
          as the last block lands.
        </Typography>

        {terms && !terms.satisfied ? (
          // The gate is the SERVER route; this only draws it. A wizard step is
          // a suggestion, and material uploaded before the gate existed can
          // never be brought under it retroactively - which is why the terms
          // are in front of the file picker rather than beside it.
          <Stack spacing={2}>
            <Alert severity="info">
              Before your first upload, please read and accept the terms below.
            </Alert>
            {terms.agreements
              .filter((entry) => entry.acceptedAt === null)
              .map((entry) => (
                <Box
                  key={entry.agreement.id}
                  sx={{
                    border: `1px solid ${tokens.divider}`,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                    {AGREEMENT_LABEL[entry.agreement.kind]} ·{" "}
                    {entry.agreement.version}
                  </Typography>
                  {entry.supersededAcceptance && (
                    // "Never seen this" and "accepted an older version" are
                    // different facts, and the same words for both would be
                    // wrong in opposite directions.
                    <Typography
                      sx={{ fontSize: 13, color: tokens.inkMuted, mb: 1 }}
                    >
                      You accepted an earlier version of this document. It has
                      changed, so we need your agreement again. Anything already
                      published stays under the version it was uploaded under.
                    </Typography>
                  )}
                  {/* The text itself, on the screen where it is accepted. A
                      link to a page somebody can edit is exactly what the
                      checksum exists to prevent. */}
                  <Box
                    sx={{
                      maxHeight: 220,
                      overflow: "auto",
                      background: tokens.canvas,
                      border: `1px solid ${tokens.divider}`,
                      borderRadius: 1,
                      p: 1.5,
                      mb: 1.5,
                      whiteSpace: "pre-wrap",
                      fontSize: 13,
                      color: tokens.ink,
                    }}
                  >
                    {entry.agreement.body}
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busy}
                    sx={{ background: tokens.brand, color: "#FFFFFF" }}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        setTerms(
                          await foyerApi.acceptAgreement(entry.agreement.id)
                        );
                      } catch (e) {
                        setError(e instanceof Error ? e.message : String(e));
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Accept
                  </Button>
                </Box>
              ))}
          </Stack>
        ) : null}

        <input
          ref={fileInput}
          type="file"
          accept="video/mp4"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
            e.target.value = "";
          }}
        />
        <Button
          variant="contained"
          disabled={busy || (terms !== null && !terms.satisfied)}
          onClick={() => fileInput.current?.click()}
          sx={{ background: tokens.brand, color: "#FFFFFF" }}
        >
          Choose a file
        </Button>

        {sent && (
          <Box sx={{ mt: 2 }}>
            {/* Determinate, because the number is real: these are bytes the
                browser has finished sending. */}
            <LinearProgress
              variant="determinate"
              value={Math.round((sent.done / Math.max(1, sent.total)) * 100)}
            />
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mt: 0.5 }}>
              Uploading — {Math.round(sent.done / 1_048_576)} MB of{" "}
              {Math.round(sent.total / 1_048_576)} MB sent
            </Typography>
          </Box>
        )}
      </Box>

      {/* ART-8. Until now a supplier could add artwork ONLY while creating a
          title, inside the wizard — finish it, come back tomorrow, and there
          was no door. The upload control is shared with that step rather than
          copied, because the thing that would drift is the surface binding,
          and a supplier call against a staff URL 403s where the shell cannot
          explain it. */}
      <Box
        sx={{
          background: tokens.surface,
          border: `1px solid ${tokens.divider}`,
          borderRadius: 2,
          p: 3,
        }}
        data-testid="title-artwork"
      >
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Artwork</Typography>
        <Typography sx={{ color: tokens.inkMuted, fontSize: 14, mb: 1.5 }}>
          What a guest sees before they press play. Pick a language, then click
          a square to choose a picture.
        </Typography>

        {full === null ? (
          <CircularProgress size={16} />
        ) : (
          <>
            {/**
             * One door, matching the wizard. The picker that used to sit here
             * always appended a `poster` in a derived language; the panel puts
             * the upload on the row the operator actually clicked, in that
             * row's language and type.
             */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                size="large"
                onClick={() => setArtworkPanel(true)}
                data-testid="title-manage-artwork"
              >
                Manage artwork
              </Button>
            </Stack>
            <Typography
              sx={{ color: tokens.inkMuted, fontSize: 14, mt: 1.5 }}
              data-testid="title-artwork-count"
            >
              {(full.images ?? []).length} image
              {(full.images ?? []).length === 1 ? "" : "s"}.
            </Typography>
          </>
        )}
      </Box>

      <LocalisedImagesPanel
        open={artworkPanel}
        images={full ? assetToDraft(full).images : []}
        assetId={full?.assetId ?? ""}
        surface="owner"
        onUploaded={(saved) => {
          // This page holds the row itself rather than a draft, so the whole
          // row IS the state — no partial merge to make, and nothing unsaved
          // beside it to lose.
          setFull(saved);
          setError(null);
        }}
        onClose={() => setArtworkPanel(false)}
        onSave={(images) => {
          setArtworkPanel(false);
          if (!full) return;
          void (async () => {
            try {
              /**
               * ⚠️ Through `draftToUpdate` against the row it was SEEDED FROM,
               * not a hand-shaped body. PATCH is destructive-replace on seven
               * collections upstream, and `draftToUpdate` states a collection
               * only when it differs from what was read — a hand-rolled
               * `{ images }` would be a second payload shaper to keep in step
               * with the wizard's, which is how the two quietly disagree.
               */
              /**
               * 🚨 And it CONFIRMS the wipe, because removing the last picture
               * is the whole reason this panel has a Remove. Without it the
               * server answers `collection_would_be_emptied` — correctly, that
               * guard is for a form that never loaded the panel — and nothing
               * on this page could say "yes, I meant it". A supplier who
               * uploaded the wrong poster was stuck with it (`ART-8`).
               */
              const next = { ...assetToDraft(full), images };
              const saved = await foyerApi.updateOwnTitle(
                full.assetId,
                draftToUpdate(next, full, {
                  confirmEmptyCollections:
                    emptiedCollections(next, full).length > 0,
                })
              );
              setFull(saved);
              setError(null);
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            }
          })();
        }}
      />

      <Box
        sx={{
          background: tokens.surface,
          border: `1px solid ${tokens.divider}`,
          borderRadius: 2,
          p: 3,
        }}
      >
        <Stack
          direction="row"
          alignItems="baseline"
          spacing={1.5}
          sx={{ mb: 0.5 }}
        >
          <Typography sx={{ fontWeight: 700 }}>Where it is showing</Typography>
          {/* Provenance, in the studio's language. A partner-facing number with
              no provenance is a number they will quote back at us - and this
              chip disappears by itself when a real source is registered. */}
          {reach?.preview && (
            <Chip
              size="small"
              label="Preview — placement data is not live yet"
              sx={{ background: tokens.brandTintSoft, color: tokens.ink }}
            />
          )}
        </Stack>

        {reachError ? (
          // Never a zero. "We cannot answer" and "nowhere" are different facts,
          // and only one of them is safe to show a partner we owe a share to.
          <Alert severity="error" sx={{ fontSize: 13, mt: 1 }}>
            We could not work out where this title is showing right now.
          </Alert>
        ) : !reach ? (
          <CircularProgress size={16} sx={{ mt: 1 }} />
        ) : reach.total === 0 ? (
          <Typography sx={{ color: tokens.inkMuted, fontSize: 14 }}>
            Not placed anywhere yet. Placement is decided by Nenda; ask your
            contact if you expected this title to be out.
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {reach.entries.map((entry) => (
              <Typography
                key={`${entry.kind}:${entry.id}`}
                sx={{ fontSize: 14, color: tokens.ink }}
              >
                {entry.label}
                {entry.premiseCount !== undefined && (
                  <Typography
                    component="span"
                    sx={{ fontSize: 13, color: tokens.inkMuted, ml: 1 }}
                  >
                    {entry.premiseCount} locations
                  </Typography>
                )}
              </Typography>
            ))}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          background: tokens.surface,
          border: `1px solid ${tokens.divider}`,
          borderRadius: 2,
          p: 3,
        }}
      >
        <Stack
          direction="row"
          alignItems="baseline"
          spacing={1.5}
          sx={{ mb: 0.5 }}
        >
          <Typography sx={{ fontWeight: 700 }}>Rentals</Typography>
          {rentals?.preview && (
            <Chip
              size="small"
              label="Preview — rental data is not connected yet"
              sx={{ background: tokens.brandTintSoft, color: tokens.ink }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {rentals && rentals.total > 0 && (
            // A studio reconciling a revenue share wants the rows, not the
            // total. Present from the first version, so its columns are the
            // ones they will still have next year.
            <Button
              size="small"
              component="a"
              href={foyerApi.contentRentalsCsvUrl(assetId)}
              sx={{ color: tokens.brand }}
            >
              Download CSV
            </Button>
          )}
        </Stack>

        {rentalError ? (
          <Alert severity="error" sx={{ fontSize: 13, mt: 1 }}>
            We could not read the rental record for this title.
          </Alert>
        ) : !rentals ? (
          <CircularProgress size={16} sx={{ mt: 1 }} />
        ) : rentals.total === 0 ? (
          <Typography sx={{ color: tokens.inkMuted, fontSize: 14 }}>
            No rentals recorded yet. Rentals are handled outside this system for
            now, so ask your Nenda contact if you are expecting some.
          </Typography>
        ) : (
          <>
            <Typography sx={{ color: tokens.inkMuted, fontSize: 14, mb: 1 }}>
              {rentals.total} rental{rentals.total === 1 ? "" : "s"},{" "}
              {rentals.active} currently licensed
              {Object.entries(rentals.gross).map(([currency, amount]) => (
                // Never summed across currencies: adding SEK to EUR produces a
                // number that is wrong in a way nobody notices until an invoice.
                <span key={currency}>
                  {" "}
                  · {amount.toLocaleString("sv-SE")} {currency}
                </span>
              ))}
            </Typography>
            <Stack spacing={0.5}>
              {rentals.rentals.map((rental, i) => (
                <Typography
                  key={`${rental.customer.id}-${i}`}
                  sx={{ fontSize: 14, color: tokens.ink }}
                >
                  {rental.customer.name}
                  <Typography
                    component="span"
                    sx={{ fontSize: 13, color: tokens.inkMuted, ml: 1 }}
                  >
                    {rental.customer.kind} · {rental.customer.country} ·{" "}
                    {rental.licenceFrom.slice(0, 10)} →{" "}
                    {rental.licenceTo.slice(0, 10)}
                  </Typography>
                </Typography>
              ))}
            </Stack>
          </>
        )}
      </Box>

      {jobs.map((job) => (
        <Box
          key={job.id}
          data-testid={`supplier-job-${job.id}`}
          sx={{
            background: tokens.surface,
            border: `1px solid ${tokens.divider}`,
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 2 }}>
            {job.sourceObjectName}
          </Typography>

          <Stack spacing={1.5}>
            {LADDER.map((step) => {
              const state = ladderStepState(job, step);
              // Only a phase has a log entry; the review rung has no duration
              // to report because no upstream timed it.
              const entry =
                step.kind === "phase"
                  ? job.phaseLog.find((e) => e.phase === step.phase)
                  : undefined;
              return (
                <Stack
                  key={step.kind === "phase" ? step.phase : "review"}
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                >
                  <Box sx={{ pt: 0.4 }}>
                    <StepMark state={state} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: state === "waiting" ? 400 : 700,
                        color:
                          state === "waiting" ? tokens.inkFaint : tokens.ink,
                      }}
                    >
                      {step.label}
                      {/* "Transcode took 4 min" answers most of what a
                          supplier will ask, and it is free. */}
                      {entry?.completedAt && (
                        <Typography
                          component="span"
                          sx={{ fontSize: 12, color: tokens.inkFaint, ml: 1 }}
                        >
                          {minutesBetween(entry.startedAt, entry.completedAt)}
                        </Typography>
                      )}
                    </Typography>
                    {(state === "current" || state === "failed") && (
                      <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                        {step.detail}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              );
            })}
          </Stack>

          {/* The distinction that keeps "queued" honest: waiting politely for a
              busy farm is not the same as a transcoder we cannot reach.

              🚨 It used to add "Our team has been told." Nobody was told: the
              whole mechanism is one `console.warn` in `ingestPoller`, emitted
              once per outage and read by nobody. That is the same class of
              defect as the scheduled-power/ESG claim in CLAUDE.md — a promise
              the product makes that the system does not keep — and a supplier
              waiting on a stalled title is exactly who it misleads.

              The vagueness was right (a partner is not shown our
              infrastructure gaps); the claim was not. If unready-dispatch ever
              does reach a human, say so again — and not before. */}
          {job.phase === "queued" &&
            !job.encoreJobId &&
            readiness &&
            !readiness.ready && (
              <Alert severity="info" sx={{ mt: 2, fontSize: 13 }}>
                Processing has not started yet.
              </Alert>
            )}

          {job.error && (
            <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>
              {job.error}
            </Alert>
          )}

          {job.incidents.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>
                What happened along the way
              </Typography>
              {/* Shown rather than hidden: "we retried twice and it worked" is
                  honest and reassuring. Hiding it and having the film vanish
                  next week is not. */}
              {job.incidents.map((incident, i) => (
                <Typography
                  key={`${incident.code}-${i}`}
                  sx={{ fontSize: 12, color: tokens.inkMuted }}
                >
                  {INCIDENT_WORDS[incident.code] ?? incident.code}
                  {incident.detail ? ` — ${incident.detail}` : ""}
                </Typography>
              ))}
            </Box>
          )}

          {job.phase === "failed" && (
            <Button
              size="small"
              disabled={busy}
              onClick={() => void retry(job)}
              sx={{ mt: 2, color: tokens.brand }}
            >
              {retryLabel(job)}
            </Button>
          )}
        </Box>
      ))}

      {jobs.length === 0 && (
        <Typography sx={{ color: tokens.inkMuted }}>
          No master has been uploaded for this title yet.
        </Typography>
      )}
    </Stack>
  );
}

/** "4 min" / "40 s". Rounded, never invented: both ends are recorded. */
function minutesBetween(startedAt: string, completedAt: string): string {
  const ms = Date.parse(completedAt) - Date.parse(startedAt);
  if (!Number.isFinite(ms) || ms < 0) return "";
  return ms < 90_000
    ? `${Math.round(ms / 1000)} s`
    : `${Math.round(ms / 60_000)} min`;
}
